import { useState, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  Avatar,
  Typography,
  IconButton,
  TextField,
  Button,
  useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'

import { HelperWrapper } from '../helper-wrapper'
import { IImpactLibraryBaseWithId } from 'src/types'

import QuestionMark from 'src/assets/images/question-mark.png'
import { DeleteIcon } from 'src/assets/icons'
import { useStyles } from './styles'
import { Loader } from '../loader'

export interface ImpactListProps {
  impacts: IImpactLibraryBaseWithId[]
  newImpacts: string[]
  onDeleteImpact: (id: string) => void
  onDeleteNewImpact: (name: string) => void
  onAddNewImpact: (name: string) => void
  hasTriedImpacts: boolean
  isFindImpactsLoading: boolean
  handleTryToFindImpactsOneMoreTime: () => Promise<void>
}

const ImpactList = ({
  impacts,
  newImpacts,
  onDeleteImpact,
  onDeleteNewImpact,
  onAddNewImpact,
  hasTriedImpacts,
  isFindImpactsLoading,
  handleTryToFindImpactsOneMoreTime,
}: ImpactListProps) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const classes = useStyles()
  const [adding, setAdding] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const handleAddClick = () => setAdding(true)

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleSaveClick = () => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    onAddNewImpact(trimmed)
    setInputValue('')
    setAdding(false)
  }

  const handleCancelClick = () => {
    setInputValue('')
    setAdding(false)
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        display: 'flex',
        flexDirection: 'column',
        mb: '15px',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: theme.palette.custom.grey2,
          mb: '5px',
        }}
      >
        Impacts
      </Typography>

      {/* Existing impacts */}
      {impacts.map((impact) => (
        <div key={impact.id} className={classes.selectionWrapper}>
          <Grid className={classes.selectionItem}>
            <Avatar
              alt={impact.name}
              src={impact.image_medium || impact.image}
              className={classes.image}
            />
            <Grid>
              <Typography className={`${classes.selectionText} ${classes.selectionTextDark}`}>
                <strong>{impact.name}</strong>
              </Typography>
            </Grid>
            <Grid style={{ flexGrow: 1 }} />
            <IconButton onClick={() => onDeleteImpact(impact.id)}>
              <DeleteIcon className={classes.deleteIcon} />
            </IconButton>
          </Grid>
        </div>
      ))}

      {/* New free-text impacts */}
      {newImpacts.map((text) => (
        <div key={text} className={classes.selectionWrapper}>
          <Grid className={classes.selectionItem}>
            <Avatar src={QuestionMark} alt="" className={classes.image} />
            <Grid>
              <Typography className={`${classes.selectionText} ${classes.selectionTextDark}`}>
                <strong>{text}</strong>
              </Typography>
            </Grid>
            <Grid style={{ flexGrow: 1 }} />
            <IconButton onClick={() => onDeleteNewImpact(text)}>
              <DeleteIcon className={classes.deleteIcon} />
            </IconButton>
          </Grid>
        </div>
      ))}

      {newImpacts.length === 0 && impacts.length === 0 && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              width: '100%',
              color: theme.palette.custom.warning2,
              textAlign: 'center',

              backgroundColor: 'rgba(250, 105, 113, 0.25)',
              borderRadius: '8px',
              padding: '6px 0',
            }}
          >
            No impacts has been found for your product
          </Typography>
          {!isFindImpactsLoading && !hasTriedImpacts && (
            <Button
              onClick={handleTryToFindImpactsOneMoreTime}
              variant="text"
              size="small"
              sx={{ color: theme.palette.custom.warning2 }}
            >
              Try again
            </Button>
          )}
          {isFindImpactsLoading && <Loader isStatic size={25} />}
        </Box>
      )}

      {/* Add new impact */}
      {adding ? (
        <div className={classes.selectionWrapper}>
          <Grid className={classes.selectionItem}>
            <Avatar src={QuestionMark} alt="" className={classes.image} />
            <Grid style={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={t('scan-to-earn-add-new-impact-input-placeholder.v')}
                value={inputValue}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </Grid>

            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <IconButton onClick={handleSaveClick} disabled={!inputValue.trim()}>
                <CheckIcon
                  sx={{
                    color: inputValue.trim() ? theme.palette.custom.primary : '',
                    width: 'auto',
                    height: '28px',
                  }}
                />
              </IconButton>

              <IconButton onClick={handleCancelClick}>
                <DeleteIcon className={classes.deleteIcon} />
              </IconButton>
            </Box>
          </Grid>
        </div>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddClick} variant="text">
            <HelperWrapper
              t={
                newImpacts.length === 0 && impacts.length === 0
                  ? 'scan-to-earn-add-new-impact-manually-button-label'
                  : 'scan-to-earn-add-new-impact-button-label'
              }
              isColorInherited
            />
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ImpactList
